"use client";

import * as React from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { Link } from "@/i18n/navigation";
import { User } from "@/types/user";
import { signOut } from "next-auth/react";
import { useTranslations } from "next-intl";
import { NavItem } from "@/types/blocks/base";

interface SignUserProps {
  user: User;
  isAdmin?: boolean;
}

export default function SignUser({ user, isAdmin = false }: SignUserProps) {
  const t = useTranslations();

  // Build dropdown items dynamically - removed nickname display per task 52
  const dropdownItems: NavItem[] = [
    {
      title: user.email,
    },
    {
      title: t("user.user_center"),
      url: "/my-orders",
    },

    // Only show Admin System for admin users
    ...(isAdmin ? [{
      title: t("user.admin_system"),
      url: "/admin/users",
    }] : []),
    {
      title: t("user.sign_out"),
      onClick: () => signOut(),
    },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Avatar className="size-10 cursor-pointer ring-2 ring-[hsl(38,62%,56%,0.3)] transition-all duration-300 hover:ring-[hsl(38,62%,56%,0.6)]">
          <AvatarImage src={user.avatar_url} alt={user.nickname} />
          <AvatarFallback className="bg-gradient-to-br from-[hsl(38,62%,56%)] to-[hsl(28,50%,45%)] text-[hsl(28,22%,10%)] font-semibold">
            {user.nickname?.charAt(0)?.toUpperCase() || 'U'}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="mx-4 z-50 min-w-[180px] rounded-xl border border-[hsl(28,18%,24%,0.85)] bg-gradient-to-b from-[hsl(28,22%,14%,0.98)] to-[hsl(24,20%,10%,0.99)] shadow-[0_25px_60px_hsl(0,0%,0%,0.5)] backdrop-blur-xl">
        {dropdownItems.map((item, index) => (
          <React.Fragment key={index}>
            <DropdownMenuItem
              className="flex justify-center cursor-pointer px-4 py-2.5 text-[hsl(36,40%,92%)] transition-all duration-200 hover:bg-[hsl(38,62%,56%,0.15)] focus:bg-[hsl(38,62%,56%,0.2)] focus:text-[hsl(40,75%,64%)] rounded-lg mx-1"
            >
              {item.url ? (
                <Link href={item.url as any} target={item.target} className="w-full text-center">
                  {item.title}
                </Link>
              ) : (
                <button onClick={item.onClick} className="w-full text-center">{item.title}</button>
              )}
            </DropdownMenuItem>
            {index !== dropdownItems.length - 1 && <DropdownMenuSeparator className="bg-[hsl(28,18%,24%,0.6)] mx-2" />}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
